import { format, parseISO } from 'date-fns';
import zonedDate from './zoned-date.mjs';

/**
 * Parse and validate an ISO date string
 * @param date - ISO date string
 * @returns {Date} Parsed date or current date if invalid
 */
export function safeParse(date) {
    if (date instanceof Date) {
        return date;
    }

    if (!date || typeof date !== 'string') {
        return new Date();
    }

    const parsed = parseISO(date);
    if (isNaN(parsed.getTime())) {
        console.warn(`Invalid date string: ${date}`);
        return new Date();
    }
    return parsed;
}

/**
 * Format a date string to display format (MMM d, yyyy)
 * @param date - Date string/object
 * @returns {string} Formatted date
 */
export function formatEventDate(date) {
    return format(safeParse(date), 'MMM d, yyyy');
}

/**
 * Format a date string to display format (d MMM yyyy)
 * @param date - Date string/object
 * @returns {string} Formatted date
 */
export function formatPostDate(date) {
    return format(safeParse(date), 'd MMM yyyy');
}

/**
 * Format a date string to time display format (h:mm a)
 * @param date - Date string/object
 * @returns {string} Formatted time
 */
export function formatEventTime(date) {
    return format(safeParse(date), 'h:mm a');
}

/**
 * Format a date string to month key format (MMMM yyyy)
 * @param date - Date string/object
 * @returns {string} Formatted month key
 */
export function formatMonthKey(date) {
    return format(safeParse(date), 'MMMM yyyy');
}

/**
 * Format a date string to day key format (yyyy-MM-dd)
 * @param date - Date string/object
 * @returns {string} Formatted day key
 */
export function formatDayKey(date) {
    return format(safeParse(date), 'yyyy-MM-dd');
}

/**
 * Format a date string to day
 * @param date - Date string/object
 * @returns {string} Formatted day display
 */
export function formatDay(date) {
    return format(safeParse(date), 'EEEE');
}

/**
 * Format a date string to day date display format
 * @param date - Date string/object
 * @returns {string} Formatted day display
 */
export function formatDate(date) {
    return format(safeParse(date), 'd');
}

/**
 * Format a date string to day display format (ordinal only - e.g., "st", "nd", "rd" etc.)
 * @param date - Date string/object
 * @returns {string} Formatted day display
 */
export function formatOrdinal(date) {
    const dateWithOrdinal = format(safeParse(date), 'do');

    return dateWithOrdinal.replace(/\d+/g, '');
}

/**
 * Format a date string to full date and time display (MMMM d, yyyy h:mm a)
 * @param date - Date string/object
 * @returns {string} Formatted date and time
 */
export function formatEventDateTime(date) {
    return `${formatEventDate(date)} at ${formatEventTime(date)}`;
}

/**
 * Format a date string for date picker (yyyy-MM)
 * @param date - Date string/object
 * @returns {string} Formatted month picker key
 */
export function formatMonthYearKey(date) {
    if (!date) return '';
    return format(safeParse(date), 'yyyy-MM');
}

/**
 * Get relative time of day based on hour (Morning, Afternoon, Evening, Night)
 * @param date - Date string/object
 * @returns {string} Time of day (Morning, Afternoon, Evening, Night)
 */
export function getTimeOfDay(date) {
    const hour = safeParse(date).getHours();

    if (hour >= 5 && hour < 12) {
        return 'Morning';
    } else if (hour >= 12 && hour < 17) {
        return 'Afternoon';
    } else if (hour >= 17 && hour < 21) {
        return 'Evening';
    } else {
        return 'Night';
    }
}
